import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  FaPhone,
  FaWhatsapp,
  FaShieldAlt,
  FaUsers,
  FaRunning,
  FaBrain,
  FaHeart,
  FaMedal,
  FaClock,
  FaMapMarkerAlt,
  FaTrophy,
  FaStar,
  FaArrowRight,
} from "react-icons/fa";
import JAIVEER from "../../assets/photos/jaiveer.jpg";
import GALLERY_1 from "../../assets/photos/achievement-podium.jpg";
import GALLERY_2 from "../../assets/photos/achievement-group.jpg";
import GALLERY_3 from "../../assets/photos/achievement-medals.jpg";
import GALLERY_4 from "../../assets/photos/achievement-boys.jpg";
import GALLERY_5 from "../../assets/photos/holi-celebration.jpg";
import GALLERY_6 from "../../assets/photos/training-session.jpg";
import GALLERY_7 from "../../assets/photos/team-trophy.jpg";
import GALLERY_8 from "../../assets/photos/school-competition.jpg";
import CHRISTMAS from "../../assets/photos/christmas-fun.jpg";
import COACH_KIDS from "../../assets/photos/coach-with-kids.jpg";
import EVENT from "../../assets/photos/event-inauguration.jpg";
import PRO_COACH from "../../assets/photos/coach-handstand-training.jpg";
import PRO_SCORPION from "../../assets/photos/kid-scorpion-pose.jpg";
import PRO_PLANCHE from "../../assets/photos/kid-planche-strength.jpg";
import PRO_GROUP from "../../assets/photos/group-bridge-training.jpg";

const benefits = [
  {
    icon: <FaShieldAlt />,
    title: "Discipline & Focus",
    desc: "Self-discipline, patience, and concentration — skills for school and life.",
  },
  {
    icon: <FaRunning />,
    title: "Balance & Coordination",
    desc: "Motor skills and body awareness through structured training.",
  },
  {
    icon: <FaBrain />,
    title: "Builds Confidence",
    desc: "Every new skill mastered boosts your child's self-esteem.",
  },
  {
    icon: <FaUsers />,
    title: "Social Skills",
    desc: "Teamwork, friendships, and communication through group classes.",
  },
  {
    icon: <FaHeart />,
    title: "Physical Fitness",
    desc: "Strength, flexibility, and endurance — a full-body workout.",
  },
  {
    icon: <FaMedal />,
    title: "Sport Foundation",
    desc: "The base for all sports. Prepares kids for any athletic pursuit.",
  },
];

const programs = [
  {
    title: "Kids Gymnastics — 6 Days",
    price: "₹5,000",
    period: "/month",
    features: ["6 classes/week", "Dance Class", "Sports Training", "Age 4–16"],
    highlighted: true,
    img: GALLERY_3,
  },
  {
    title: "Kids Gymnastics — 3 Days",
    price: "₹3,500",
    period: "/month",
    features: ["3 classes/week", "Sports Training", "Focus & Discipline", "Age 4–16"],
    highlighted: false,
    img: GALLERY_6,
  },
  {
    title: "Fitness Group Class",
    price: "₹3,000",
    period: "/month",
    features: ["Group Yoga", "CrossFit & Cardio", "Strength Training", "Plyometrics"],
    highlighted: false,
    img: COACH_KIDS,
  },
];

const galleryItems = [
  { img: GALLERY_1, label: "District Championship", span: "md:col-span-2 md:row-span-2" },
  { img: GALLERY_3, label: "Medal Winners", span: "" },
  { img: GALLERY_4, label: "Golden Grip Tournament", span: "" },
  { img: CHRISTMAS, label: "Christmas Celebration", span: "md:col-span-2" },
  { img: PRO_SCORPION, label: "Flexibility Training", span: "" },
  { img: PRO_PLANCHE, label: "Strength & Balance", span: "" },
  { img: GALLERY_2, label: "State Competition", span: "md:col-span-2" },
  { img: GALLERY_8, label: "VIBGYOR School Competition", span: "" },
  { img: PRO_GROUP, label: "Group Bridge Training", span: "md:col-span-2" },
  { img: GALLERY_6, label: "Training Session", span: "" },
  { img: GALLERY_7, label: "Team Trophy", span: "" },
  { img: EVENT, label: "Academy Inauguration", span: "" },
  { img: GALLERY_5, label: "Holi with Students", span: "md:col-span-2" },
  { img: PRO_COACH, label: "Coach Handstand Training", span: "" },
  { img: COACH_KIDS, label: "Coach with Students", span: "" },
];

const Home = () => {
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });

  const openLightbox = (i) => setLightbox({ open: true, index: i });
  const closeLightbox = () => setLightbox({ open: false, index: 0 });
  const openByImage = (imgSrc) => {
    const idx = galleryItems.findIndex((item) => item.img === imgSrc);
    if (idx !== -1) setLightbox({ open: true, index: idx });
  };
  const prevImage = useCallback(() => setLightbox((s) => ({ ...s, index: (s.index - 1 + galleryItems.length) % galleryItems.length })), []);
  const nextImage = useCallback(() => setLightbox((s) => ({ ...s, index: (s.index + 1) % galleryItems.length })), []);

  useEffect(() => {
    if (!lightbox.open) return;
    const handleKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") prevImage();
      if (e.key === "ArrowRight") nextImage();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKey);
    };
  }, [lightbox.open, prevImage, nextImage]);

  return (
    <>
      {/* === HERO — Full-width cinematic === */}
      <section className="relative min-h-screen flex items-end overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src={PRO_COACH}
            alt="Coach Jaiveer giving personal training at Flipcity Gymnastics Academy"
            className="w-full h-full object-cover object-[50%_20%]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent" />
        </div>

        {/* Hero content */}
        <div className="relative z-10 w-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-24 pt-32">

            {/* Floating stats — top right of content area */}
            <div className="hidden md:flex flex-col gap-3 absolute top-28 right-8 lg:right-12">
              {[
                { val: "25+", label: "Years Exp." },
                { val: "100+", label: "Students" },
                { val: "50+", label: "Medals" },
              ].map((s, i) => (
                <div
                  key={i}
                  className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-5 py-3.5 text-center text-white"
                >
                  <p className="text-2xl font-extrabold leading-none">
                    {s.val}
                  </p>
                  <p className="text-[10px] text-white/70 mt-0.5 uppercase tracking-wider">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-xs font-medium px-4 py-2 rounded-full mb-6">
              <FaMapMarkerAlt />
              Sector 93, Gurgaon &bull; NIS Certified Academy
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-7xl font-black text-white leading-[1.1] max-w-3xl">
              Building
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">
                Champions
              </span>
              <br />
              One Flip at a Time
            </h1>

            <p className="mt-6 text-base md:text-lg text-white/70 max-w-xl leading-relaxed">
              Gymnastics academy for kids aged 4–16. Where discipline meets fun
              and every child discovers their inner champion.
            </p>

            {/* Mobile stats row */}
            <div className="flex md:hidden gap-4 mt-6">
              {[
                { val: "25+", label: "Years" },
                { val: "100+", label: "Students" },
                { val: "50+", label: "Medals" },
              ].map((s, i) => (
                <div key={i} className="text-center">
                  <p className="text-xl font-extrabold text-white leading-none">{s.val}</p>
                  <p className="text-[10px] text-white/60 uppercase tracking-wider mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>

            {/* CTA row */}
            <div className="flex flex-wrap items-center gap-4 mt-8">
              <a
                href="https://wa.me/919582691655?text=Hi%2C%20I%27d%20like%20to%20book%20a%20free%20trial%20class"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-3 bg-white text-slate-900 font-bold px-7 py-4 rounded-full hover:bg-blue-50 transition-all shadow-xl"
              >
                Book Free Trial
                <FaArrowRight className="text-sm group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="tel:+919582691655"
                className="inline-flex items-center gap-2 text-white/90 hover:text-white font-medium px-5 py-4 rounded-full border border-white/30 hover:border-white/60 backdrop-blur-sm transition-all"
              >
                <FaPhone className="text-sm" />
                +91 95826 91655
              </a>
            </div>

            {/* Scrolling image strip */}
            <div className="mt-12 flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
              {[PRO_GROUP, GALLERY_1, PRO_SCORPION, CHRISTMAS, GALLERY_7].map(
                (img, i) => (
                  <div
                    key={i}
                    className="flex-shrink-0 w-28 h-20 md:w-40 md:h-28 rounded-xl overflow-hidden border-2 border-white/20 cursor-pointer"
                    onClick={() => openByImage(img)}
                  >
                    <img
                      src={img}
                      alt=""
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </section>

      {/* === WHY GYMNASTICS */}
      <section className="py-24 bg-slate-950 relative overflow-hidden">
        {/* Subtle bg pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "40px 40px" }} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left — image collage */}
            <div className="relative hidden lg:block">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="rounded-2xl overflow-hidden h-52 cursor-pointer" onClick={() => openByImage(GALLERY_1)}>
                    <img src={GALLERY_1} alt="Championship podium" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="rounded-2xl overflow-hidden h-72 cursor-pointer" onClick={() => openByImage(PRO_COACH)}>
                    <img src={PRO_COACH} alt="Coach training handstand" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                  </div>
                </div>
                <div className="space-y-4 pt-8">
                  <div className="rounded-2xl overflow-hidden h-72 cursor-pointer" onClick={() => openByImage(PRO_SCORPION)}>
                    <img src={PRO_SCORPION} alt="Kid doing scorpion pose" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="rounded-2xl overflow-hidden h-52 cursor-pointer" onClick={() => openByImage(GALLERY_3)}>
                    <img src={GALLERY_3} alt="Winning medals" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                  </div>
                </div>
              </div>
              {/* Floating accent card */}
              <div className="absolute -bottom-6 -right-4 bg-gradient-to-br from-primary-500 to-blue-600 text-white rounded-2xl px-6 py-4 shadow-2xl shadow-primary-500/30">
                <p className="text-3xl font-black leading-none">25+</p>
                <p className="text-xs text-white/80 mt-1 uppercase tracking-wider">Years of Excellence</p>
              </div>
            </div>

            {/* Right — content */}
            <div>
              <p className="text-blue-400 font-semibold text-sm uppercase tracking-wider mb-3">
                Why Gymnastics
              </p>
              <h2 className="text-3xl md:text-5xl font-black text-white leading-tight mb-4">
                More Than a Sport.
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">
                  A Life Foundation.
                </span>
              </h2>
              <p className="text-slate-400 mb-10 max-w-md leading-relaxed">
                Gymnastics builds the physical and mental skills every child
                needs to thrive — from the classroom to the playground.
              </p>

              <div className="space-y-1">
                {benefits.map((b, i) => (
                  <div
                    key={i}
                    className="group flex items-start gap-5 p-4 rounded-2xl hover:bg-white/5 transition-colors duration-300 cursor-default"
                  >
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm border border-white/10 flex items-center justify-center text-blue-400 text-lg group-hover:bg-gradient-to-br group-hover:from-primary-500 group-hover:to-blue-600 group-hover:text-white group-hover:border-transparent group-hover:scale-110 transition-all duration-300">
                      {b.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-base font-bold text-white group-hover:text-blue-300 transition-colors">
                          {b.title}
                        </h3>
                        <span className="text-[10px] font-bold text-white/20 uppercase tracking-widest">
                          0{i + 1}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 leading-relaxed group-hover:text-slate-400 transition-colors">
                        {b.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Mobile image strip */}
              <div className="mt-10 flex gap-3 overflow-x-auto lg:hidden pb-2 -mx-4 px-4 scrollbar-hide">
                {[GALLERY_1, COACH_KIDS, GALLERY_5, GALLERY_3].map((img, i) => (
                  <div key={i} className="flex-shrink-0 w-32 h-24 rounded-xl overflow-hidden cursor-pointer" onClick={() => openByImage(img)}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* === BENTO GALLERY === */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <p className="text-blue-400 font-semibold text-sm uppercase tracking-wider mb-2">
                Life at FlipCity
              </p>
              <h2 className="text-3xl md:text-5xl font-black text-white leading-tight">
                Moments That
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">
                  Define Us
                </span>
              </h2>
            </div>
            <div className="flex items-center gap-6 text-white/50">
              <div className="text-center">
                <p className="text-3xl font-black text-white">50+</p>
                <p className="text-xs uppercase tracking-wider">Medals</p>
              </div>
              <div className="w-px h-10 bg-white/20" />
              <div className="text-center">
                <p className="text-3xl font-black text-white">100+</p>
                <p className="text-xs uppercase tracking-wider">Students</p>
              </div>
              <div className="w-px h-10 bg-white/20" />
              <div className="text-center">
                <p className="text-3xl font-black text-white">25+</p>
                <p className="text-xs uppercase tracking-wider">Years</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[180px] md:auto-rows-[220px] gap-3">
            {galleryItems.map((item, i) => (
              <div
                key={i}
                className={`group relative rounded-2xl overflow-hidden cursor-pointer ${item.span}`}
                onClick={() => openLightbox(i)}
              >
                <img
                  src={item.img}
                  alt={item.label}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 md:translate-y-4 md:group-hover:translate-y-0 md:opacity-0 md:group-hover:opacity-100 transition-all duration-300">
                  <p className="text-white text-xs md:text-sm font-semibold drop-shadow-lg">
                    {item.label}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === PROGRAMS — with images === */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-blue-400 font-semibold text-sm uppercase tracking-wider mb-2">
              Programs
            </p>
            <h2 className="text-3xl md:text-5xl font-black text-white">
              Choose Your Path
            </h2>
            <p className="mt-4 text-slate-400">
              Transparent pricing. No hidden fees. Real results.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {programs.map((p, i) => (
              <div
                key={i}
                className={`group relative rounded-3xl overflow-hidden ${
                  p.highlighted ? "ring-2 ring-primary-500 ring-offset-4 ring-offset-slate-900" : ""
                }`}
              >
                {/* Card image */}
                <div className="h-48 overflow-hidden">
                  <img
                    src={p.img}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {p.highlighted && (
                    <div className="absolute top-4 left-4 bg-primary-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                      Most Popular
                    </div>
                  )}
                </div>
                {/* Card content */}
                <div className="p-6 bg-slate-800 border border-t-0 border-slate-700/50 rounded-b-3xl">
                  <h3 className="text-lg font-bold text-white mb-1">
                    {p.title}
                  </h3>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-3xl font-black text-blue-400">
                      {p.price}
                    </span>
                    <span className="text-sm text-slate-500">{p.period}</span>
                  </div>
                  <ul className="space-y-2 mb-6">
                    {p.features.map((f, j) => (
                      <li
                        key={j}
                        className="flex items-center gap-2 text-sm text-slate-400"
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/pricing"
                    className={`block text-center py-3 rounded-xl font-bold text-sm transition-all ${
                      p.highlighted
                        ? "bg-primary-600 text-white hover:bg-primary-700 shadow-lg shadow-primary-600/25"
                        : "bg-slate-700 text-white hover:bg-primary-600"
                    }`}
                  >
                    Get Started
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === FOUNDER & VISION === */}
      <section className="relative overflow-hidden">
        <div className="grid md:grid-cols-2 min-h-[700px]">
          {/* Image side */}
          <div className="relative h-96 md:h-auto">
            <img
              src={JAIVEER}
              alt="Jaiveer Yadav — Founder, Flipcity Gymnastics Academy"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-l from-slate-950 via-slate-950/40 to-transparent" />
            {/* Quote overlay on image */}
            <div className="absolute bottom-6 left-6 right-6 md:bottom-10 md:left-10 md:right-10">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-5 md:p-6">
                <p className="text-white text-sm md:text-base italic leading-relaxed">
                  "In the army, gymnastics taught me discipline, courage, and
                  resilience. I started Flipcity so every child in Gurgaon can
                  discover that same strength — before life demands it from
                  them."
                </p>
                <p className="text-blue-300 text-xs font-bold mt-3 uppercase tracking-wider">
                  — Jaiveer Yadav, Founder
                </p>
              </div>
            </div>
          </div>

          {/* Content side */}
          <div className="bg-slate-950 text-white flex items-center">
            <div className="px-8 md:px-16 py-16 max-w-xl">
              <p className="text-blue-400 font-semibold text-sm uppercase tracking-wider mb-3">
                Founder & Head Coach
              </p>
              <h2 className="text-3xl md:text-5xl font-black leading-tight mb-2">
                Jaiveer Yadav
              </h2>
              <div className="flex items-center gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <FaStar key={i} className="text-amber-400 text-sm" />
                ))}
                <span className="text-white/50 text-sm ml-2">
                  25+ Years Experience
                </span>
              </div>

              {/* Vision */}
              <div className="mb-8">
                <h3 className="text-lg font-bold text-white mb-3">The Vision</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-4">
                  After 17 years serving in the Indian Army as a gymnast and
                  coach, Jaiveer saw a gap — thousands of children in New
                  Gurgaon's growing societies with energy, potential, but no
                  structured platform to channel it. Flipcity was born from a
                  simple belief:
                </p>
                <p className="text-white font-semibold leading-relaxed border-l-2 border-blue-400 pl-4">
                  Gymnastics isn't just a sport — it's a life system. It builds
                  discipline before exams demand it, confidence before interviews
                  test it, and resilience before life challenges it.
                </p>
              </div>

              {/* What it brings */}
              <div className="mb-8">
                <h3 className="text-lg font-bold text-white mb-3">
                  Transforming Young Lives
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  In a world of screens and short attention spans, gymnastics
                  gives children something rare — focus, body awareness, and the
                  habit of showing up every single day. Kids who train here carry
                  that discipline into school, relationships, and eventually
                  their careers. That's the real medal.
                </p>
              </div>

              {/* Credentials */}
              <div className="flex flex-wrap gap-3 mb-8">
                {[
                  { icon: <FaMedal />, text: "NIS Patiala Diploma" },
                  { icon: <FaShieldAlt />, text: "17 Yrs Indian Army" },
                  { icon: <FaRunning />, text: "Physical Conditioner" },
                ].map((c, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2"
                  >
                    <span className="text-blue-400 text-sm">{c.icon}</span>
                    <span className="text-xs font-medium text-slate-300">
                      {c.text}
                    </span>
                  </div>
                ))}
              </div>

              <Link
                to="/contact"
                className="group inline-flex items-center gap-3 bg-white text-slate-900 font-bold px-7 py-4 rounded-full hover:bg-blue-50 transition-all"
              >
                Get in Touch
                <FaArrowRight className="text-sm group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* === LOCATION + LOCAL SEO === */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-900 rounded-3xl border border-slate-800 overflow-hidden">
            <div className="grid md:grid-cols-2">
              <div className="p-10 md:p-14 flex flex-col justify-center">
                <p className="text-blue-400 font-semibold text-sm uppercase tracking-wider mb-3">
                  Sector 93, New Gurgaon
                </p>
                <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
                  Your Neighbourhood Gymnastics Academy
                </h2>
                <p className="text-slate-400 text-sm mb-6 leading-relaxed">
                  Located on IMT Road in the heart of Sector 93 — one of New
                  Gurgaon's fastest-growing residential areas. Just minutes from
                  DLF Garden City, Signature Global City 93, Spaze Privvy,
                  Amarah by Ashiana, and MRG World The Balcony.
                </p>

                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-blue-400 flex-shrink-0">
                      <FaMapMarkerAlt />
                    </div>
                    <div>
                      <p className="font-semibold text-white">Address</p>
                      <p className="text-sm text-slate-400">
                        IMT Rd, near Jotram Chowk, Hayatpur, Sector 93,
                        Gurugram, Haryana 122505
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-blue-400 flex-shrink-0">
                      <FaClock />
                    </div>
                    <div>
                      <p className="font-semibold text-white">Timings</p>
                      <p className="text-sm text-slate-400">
                        Mon – Sat: 6:00 AM – 9:00 PM
                      </p>
                    </div>
                  </div>
                </div>

                {/* Nearby societies tags */}
                <div className="mb-6">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                    Serving families from
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      "DLF Garden City",
                      "Signature Global City 93",
                      "Spaze Privvy",
                      "Amarah by Ashiana",
                      "Orchard Avenue",
                      "Sector 89–94",
                    ].map((s, i) => (
                      <span
                        key={i}
                        className="bg-white/10 text-slate-300 text-xs px-3 py-1.5 rounded-full border border-white/5"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <a
                    href="tel:+919582691655"
                    className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-bold text-sm px-6 py-3 rounded-full transition-colors"
                  >
                    <FaPhone className="text-xs" />
                    Call Now
                  </a>
                  <a
                    href="https://maps.google.com/?q=Flipcity+Gymnastics+Academy+Sector+93+Gurgaon"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-white/10 text-white hover:bg-white/20 font-bold text-sm px-6 py-3 rounded-full transition-colors border border-white/10"
                  >
                    <FaMapMarkerAlt className="text-xs" />
                    Get Directions
                  </a>
                </div>
              </div>
              <div>
                <iframe
                  title="Flipcity Gymnastics Academy Sector 93 Gurgaon"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3509.5!2d76.9315259!3d28.4185384!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d177e1ff7cb99%3A0xdfd6767b9ac0defd!2sFlipcity%20Gymnastics%20Academy!5e0!3m2!1sen!2sin!4v1709900000000"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: "400px" }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="w-full h-full"
                />
              </div>
            </div>
          </div>

          {/* Local SEO — nearby landmarks */}
          <div className="mt-10 grid sm:grid-cols-3 gap-6 text-center">
            <div className="bg-slate-900 rounded-2xl p-6 border border-slate-800">
              <p className="text-2xl font-black text-blue-400">5–10 min</p>
              <p className="text-sm text-slate-500 mt-1">
                From RPS International, St. Xaviers &amp; Pranavananda School
              </p>
            </div>
            <div className="bg-slate-900 rounded-2xl p-6 border border-slate-800">
              <p className="text-2xl font-black text-blue-400">6 km</p>
              <p className="text-sm text-slate-500 mt-1">
                From IMT Manesar via IMT Road
              </p>
            </div>
            <div className="bg-slate-900 rounded-2xl p-6 border border-slate-800">
              <p className="text-2xl font-black text-blue-400">10 min</p>
              <p className="text-sm text-slate-500 mt-1">
                From Garhi Harsaru Junction Railway Station
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* === FREE TRIAL CTA === */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={GALLERY_2}
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-primary-900/85 backdrop-blur-sm" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white text-xs font-medium px-4 py-2 rounded-full mb-6 border border-white/20">
            <FaTrophy className="text-amber-400" />
            2 Free Trial Classes
          </div>
          <h2 className="text-3xl md:text-5xl font-black text-white mb-4">
            Not Sure Yet?
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">
              Experience It First.
            </span>
          </h2>
          <p className="text-lg text-white/70 mb-10 max-w-xl mx-auto">
            Let your child try 2 classes free. No commitment, no pressure — just
            pure gymnastics fun.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://wa.me/919582691655?text=Hi%2C%20I%27d%20like%20to%20book%20a%20free%20trial%20class%20for%20my%20child"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 bg-white text-slate-900 font-bold px-8 py-4 rounded-full hover:bg-blue-50 transition-all shadow-xl text-lg"
            >
              <FaWhatsapp className="text-green-500 text-xl" />
              Book Free Trial
              <FaArrowRight className="text-sm group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>
      {/* === LIGHTBOX MODAL === */}
      {lightbox.open && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-5 right-5 text-white/70 hover:text-white text-3xl z-10 w-10 h-10 flex items-center justify-center"
            aria-label="Close lightbox"
          >
            ✕
          </button>

          {/* Counter */}
          <div className="absolute top-5 left-5 text-white/50 text-sm font-medium">
            {lightbox.index + 1} / {galleryItems.length}
          </div>

          {/* Prev button */}
          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-4 md:left-8 text-white/50 hover:text-white text-4xl z-10 w-12 h-12 flex items-center justify-center hover:bg-white/10 rounded-full transition-colors"
            aria-label="Previous image"
          >
            ‹
          </button>

          {/* Image */}
          <div
            className="max-w-5xl max-h-[85vh] px-4 md:px-16"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={galleryItems[lightbox.index].img}
              alt={galleryItems[lightbox.index].label}
              className="max-w-full max-h-[80vh] object-contain mx-auto rounded-lg"
            />
            <p className="text-center text-white/70 text-sm mt-4 font-medium">
              {galleryItems[lightbox.index].label}
            </p>
          </div>

          {/* Next button */}
          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-4 md:right-8 text-white/50 hover:text-white text-4xl z-10 w-12 h-12 flex items-center justify-center hover:bg-white/10 rounded-full transition-colors"
            aria-label="Next image"
          >
            ›
          </button>
        </div>
      )}
    </>
  );
};

export default Home;
